import { EditBoundaryLayer } from "./edit-boundary-layer"
import { GcpContext } from "./gcp-context"
import { TargetMap } from "./target-map"
import { IBoundaryEditResult } from "./types"

/// <reference path="edit-boundary-layer.ts" />

import IMapTopologyService = omnia.map.IMapTopologyService
import IMapMeasuringService = omnia.map.IMapMeasuringService
import { ol_Feature, ol_Map } from "@omnia/exports/openlayers"

export class EditBoundaryOperation {


    static start(
        context: GcpContext,
        target: TargetMap,
        topo: IMapTopologyService,
        measure: IMapMeasuringService,
        stop: (result: IBoundaryEditResult) => void,
        apply: () => void,
        timeout: (fn: () => void) => void): EditBoundaryOperation {

        let op = new EditBoundaryOperation(context, target, topo, measure, stop, apply, timeout);

        context.editMode = false;
        target.showFieldBoundary = true;

        op.startSplit();

        return op;

    }

    private olmap: ol_Map;
    private drawingLayer: EditBoundaryLayer;

    private _canRevert: boolean;

    private selectInteraction: omnia.map.MapSelectInteraction;
    private editInteraction: omnia.map.MapEditInteraction;

    private _splitting: boolean;
    private _limit: ol_Feature;
    private _splits: ol_Feature[];

    private _selectedSplit: ol_Feature;


    constructor(
        private context: GcpContext,
        private target: TargetMap,
        private topo: IMapTopologyService,
        private measure: IMapMeasuringService,
        private _stop: (result: IBoundaryEditResult) => void,
        private _apply: () => void,
        private _timeout: (fn: () => void) => void) {

        this.olmap = target.getMap();

        this.setup();

    }

    private dispose() {

        this.cancelEditMode();

        this.target.removeDrawingLayer();

        this.selectInteraction.dispose();
        this.selectInteraction = null;

        this.editInteraction.dispose();
        this.editInteraction = null;

        this.context.editMode = true;
        this._limit = null;
        this._splitting = false;

    }


    private setup() {

        this.drawingLayer = new EditBoundaryLayer();

        this.target.insertDrawingLayer(this.drawingLayer.mapLayer);

        this.selectInteraction = new omnia.map.MapSelectInteraction(this.olmap);
        this.selectInteraction.setStyleProvider((feature, resolution) => omnia.map.Layer.getSelectedStyle(feature, resolution));
        this.selectInteraction.addLayer(this.drawingLayer);
        this.selectInteraction.watchSelection(features => this.watchSelection(features))

        this.editInteraction = new omnia.map.MapEditInteraction(
            this.olmap,
            this.selectInteraction,
            this._timeout,
            this.measure
        );

        this._canRevert = this.target.hasCustomBoundary();

    }

    startDrawingSplitLimit(watcher: (feature: ol_Feature) => boolean) {

        this.editInteraction.startDrawing(this.drawingLayer.mapLayer, "LineString", {
            moveLayerToTop: false,
            drawEndWatcher: watcher
        });

    }

    private cancelEditMode() {
        this.editInteraction.cancelAllEdits();
        this.drawingLayer.mapLayer.getSource().clear();
    }

    public startSplit() {
        this._limit = null;
        this._splitting = true;
        this.startDrawingSplitLimit(feature => this.setNewSplitLimit(feature));
    }

    isSplitting(): boolean {
        return this._splitting;
    }

    private setNewSplitLimit(limit: ol_Feature): boolean {
        this._limit = limit;
        return true;
    }

    public cancel() {
        this.dispose();
        this._stop(null);
    }

    public commit() {

        let geom = this._selectedSplit ? this._selectedSplit.getGeometry() : null;

        let result: IBoundaryEditResult = {
            boundary: geom
        };

        this.dispose();
        this._stop(result);

    }

    getSelectedSplitText(): string {
        if (this._selectedSplit) {
            return this._selectedSplit.get("data");
        }
    }

    hasSplits(): boolean {
        return this._splits && (this._splits.length > 1);
    }

    canCommit() {
        return !!this._selectedSplit;
    }

    canSplit(): boolean {
        return !!this._limit;
    }

    split(): boolean {

        if (!this._limit) return false;

        let sources = [this._limit.getGeometry()];

        sources = sources.concat(this._splits ? this._splits.map(s => s.getGeometry()) : [this.target.fieldBoundary]);

        let results = this.topo.polygonize(sources);

        let splits = results
            .filter(p => (p.getDimension() == 2))
            .map(p => {
                return {
                    polygon: p,
                    area: p.getArea()
                }
            })
            .filter(p => p.area >= 5)
            .sort((p1, p2) => p2.area - p1.area)
            .map(p => p.polygon.getOpenLayersGeometry())
            .map((p, index) => {
                let f = new ol_Feature(p);
                f.set("layer", this.drawingLayer);
                f.set("data", "S" + (index + 1));
                return f;
            });

        if (splits.length > 1) {

            this._limit = null;
            this.cancelEditMode();
            this._splitting = false;
            this._splits = splits;

            let layer = this.drawingLayer.mapLayer;
            let source = layer.getSource();

            source.clear();
            source.addFeatures(splits);

        }

    }

    private watchSelection(features: ol_Feature[]) {

        if (this.hasSplits() && (!this.isSplitting())) {
            this._selectedSplit = features && (features.length == 1) ? features[0] : null;
            this._apply();
        }

    }

    canRevertToFieldBoundary(): boolean {
        return this._canRevert;
    }

    revertToFieldBoundary() {

        let result: IBoundaryEditResult = {
            boundary: null
        };

        this.dispose();
        this._stop(result);

    }
}
